import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Square,
  RotateCcw,
  Rocket,
  ExternalLink,
  SendHorizontal,
  Eye,
  Play,
  XOctagon,
  Paperclip,
  Shuffle,
  Copy,
  Hourglass,
  Wallet,
  RefreshCw,
} from 'lucide-react';
import { api, type Job, type Shot, type Attachment, type Account } from '../api.js';
import { openStream } from '../stream.js';
import { runAction, retryAction, dismissAction, useActions } from '../actions.js';
import { Screen, Section, Badge, Btn, Sheet, Field, EmptyState, Spinner, minutes, clock, ago, Sha, useNow, CostReading, modelName } from '../ui/kit.js';
import { meta, ENDING_TITLE, lifeMeta, railReached } from '../phases.js';
import { Rail } from './Jobs.js';
import { ShotGrid, type ShotGroup } from './job/ShotViewer.js';
import { LogPanel, type LogEvent } from './job/LogPanel.js';
import { FinisherHistory, Breakers, ModelControl, AlertsPanel, LongJobsPanel, LifecycleBanner, ModelMismatchBanner } from './job/FinisherPanels.js';
import { ShardGrid, PhaseTimeline, CostPanel, DiffList, ArtifactsPanel, CommitList, DeployPanel, JournalPanel, type Grid, type ArtifactRow } from './job/InsightPanels.js';

/* One job, as facts with their age (Ops V7 design §1.2, §5): whether the
 * process is alive, when it last spoke, what command it is inside, the
 * last commit, whether the branch is on origin, the PR, CI, the merge and
 * the deploy. Underneath: the way the last attempt ended with the buttons
 * that apply, the open question, the log opened at its tail, and the
 * instruments. Every request shows pending, done or failed with a retry. */

const BUTTON_LABEL: Record<string, { label: string; icon: React.ReactNode; kind: 'primary' | 'quiet' | 'danger' | 'ghost' }> = {
  continue: { label: 'Continue', icon: <Play size={15} />, kind: 'primary' },
  continue_other: { label: 'Continue on the other account', icon: <Shuffle size={15} />, kind: 'quiet' },
  continue_with_red: { label: 'Continue with the red', icon: <Play size={15} />, kind: 'primary' },
  retry: { label: 'Retry from the branch', icon: <RotateCcw size={15} />, kind: 'quiet' },
  stop: { label: 'Stop', icon: <Square size={15} />, kind: 'danger' },
  stop_command: { label: 'Stop this command', icon: <XOctagon size={15} />, kind: 'danger' },
  ship: { label: 'Review and ship', icon: <Eye size={15} />, kind: 'primary' },
  open_pr: { label: 'Open the PR', icon: <ExternalLink size={15} />, kind: 'quiet' },
  cancel: { label: 'Cancel', icon: <Square size={15} />, kind: 'danger' },
  wait: { label: 'Wait for the reset', icon: <Hourglass size={15} />, kind: 'ghost' },
};

export function JobDetail() {
  const { id = '' } = useParams();
  /* Subscribed so the elapsed time below re-renders every second while the
     job is running, rather than only when a poll lands (§11). */
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const tick = useNow(job?.state === 'running');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [models, setModels] = useState<{ id: string; label: string; cli?: string }[]>([]);
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [earlier, setEarlier] = useState(0);
  const [logError, setLogError] = useState<string | null>(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [delta, setDelta] = useState('');
  const [shots, setShots] = useState<Shot[]>([]);
  const [shotTotal, setShotTotal] = useState(0);
  const [shotDuplicates, setShotDuplicates] = useState(0);
  const [shotGroups, setShotGroups] = useState<ShotGroup[]>([]);
  const [shotsLoading, setShotsLoading] = useState(false);
  const [sideErrors, setSideErrors] = useState<Record<string, string>>({});
  const [grid, setGrid] = useState<Grid | null>(null);
  const [commits, setCommits] = useState<{ sha: string; subject: string; localOnly?: boolean }[]>([]);
  const [diff, setDiff] = useState<{ files: { file: string; additions: number; deletions: number }[]; total: { additions: number; deletions: number } } | null>(null);
  const [artifacts, setArtifacts] = useState<ArtifactRow[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [followUp, setFollowUp] = useState('');
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [costTick, setCostTick] = useState(0);
  const [railsOpen, setRailsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const actions = useActions(id);
  const lastId = useRef(0);
  const questionSeen = useRef<Set<string>>(new Set());

  const setSideError = (key: string, e: unknown) => setSideErrors((cur) => ({ ...cur, [key]: e instanceof Error ? e.message : 'This did not load.' }));
  const clearSideError = (key: string) =>
    setSideErrors((cur) => {
      if (!(key in cur)) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });

  const loadJob = () =>
    api
      .get<{ job: Job; accounts: Account[]; models?: { id: string; label: string; cli?: string }[] }>(`/api/jobs/${id}`)
      .then((r) => {
        setJob(r.job);
        setAccounts(r.accounts ?? []);
        if (r.models?.length) setModels(r.models);
        clearSideError('job');
      })
      .catch((e: unknown) => setSideError('job', e));

  const loadEarlier = async () => {
    const oldest = events[0]?.id;
    if (!oldest || loadingEarlier) return;
    setLoadingEarlier(true);
    try {
      const r = await api.get<{ events: LogEvent[]; earlier: number }>(`/api/jobs/${id}/events?before=${oldest}&limit=400`);
      setEvents((cur) => [...r.events, ...cur]);
      setEarlier(r.earlier);
      setLogError(null);
    } catch (e) {
      setLogError(e instanceof Error ? e.message : 'Could not read earlier entries.');
    } finally {
      setLoadingEarlier(false);
    }
  };

  const loadSide = () => {
    void api
      .get<{ shots: Shot[]; total: number; duplicates: number; groups: ShotGroup[] }>(`/api/jobs/${id}/shots?limit=120`)
      .then((r) => {
        setShots(r.shots);
        setShotTotal(r.total);
        setShotDuplicates(r.duplicates ?? 0);
        setShotGroups(r.groups);
        clearSideError('shots');
      })
      .catch((e: unknown) => setSideError('shots', e));
    void api
      .get<Grid>(`/api/jobs/${id}/ci/grid`)
      .then((g) => setGrid(g.shards.length || g.fastLane ? g : null))
      .catch((e: unknown) => setSideError('ci', e));
    void api
      .get<{ commits: typeof commits }>(`/api/jobs/${id}/commits`)
      .then((r) => setCommits(r.commits))
      .catch((e: unknown) => setSideError('commits', e));
    void api
      .get<NonNullable<typeof diff>>(`/api/jobs/${id}/diff`)
      .then((r) => setDiff(r.files.length ? r : null))
      .catch((e: unknown) => setSideError('diff', e));
    void api
      .get<{ artifacts: ArtifactRow[] }>(`/api/jobs/${id}/artifacts`)
      .then((r) => setArtifacts(r.artifacts))
      .catch((e: unknown) => setSideError('artifacts', e));
    void api
      .get<{ attachments: Attachment[] }>(`/api/jobs/${id}/attachments`)
      .then((r) => setAttachments(r.attachments))
      .catch((e: unknown) => setSideError('attachments', e));
  };

  useEffect(() => {
    let cancelled = false;
    let stream: { close: () => void } | null = null;
    void loadJob();
    const onEvent = (kind: string) => (data: unknown, sseId?: string) => {
      const numId = Number(sseId ?? 0);
      if (numId && numId <= lastId.current) return;
      if (numId) lastId.current = numId;
      setDelta('');
      const carried = (data as { _ts?: unknown } | null)?._ts;
      const ts = typeof carried === 'string' ? carried : new Date().toISOString();
      setEvents((e) => [...e.slice(-3000), { id: numId || Date.now(), ts, kind, payload: data }]);
      if (['phase', 'ci', 'shot', 'artifact', 'attempt', 'ending', 'question', 'answered', 'merged', 'facts', 'action'].includes(kind)) {
        void loadJob();
        if (kind !== 'question' && kind !== 'answered') loadSide();
      }
      if (kind === 'cost') {
        setCostTick((t) => t + 1);
        void loadJob();
      }
      // An open command is a fact on the panel. The `activity` frame that
      // announces it is not stored, so a page that connects a moment late
      // never sees it; the tool events are stored and replayed, and they
      // bracket the same fact.
      if (kind === 'activity' || kind === 'tool_use' || kind === 'tool_result') void loadJob();
    };
    const openLive = () => {
      if (cancelled) return;
      stream = openStream(
        `/api/jobs/${id}/stream`,
        {
          assistant_text: onEvent('assistant_text'),
          tool_use: onEvent('tool_use'),
          tool_result: onEvent('tool_result'),
          system: onEvent('system'),
          phase: onEvent('phase'),
          attempt: onEvent('attempt'),
          ending: onEvent('ending'),
          question: onEvent('question'),
          answered: onEvent('answered'),
          followup: onEvent('followup'),
          delivered: onEvent('delivered'),
          signal: onEvent('signal'),
          turn: onEvent('turn'),
          merged: onEvent('merged'),
          action: onEvent('action'),
          ci: onEvent('ci'),
          shot: onEvent('shot'),
          artifact: onEvent('artifact'),
          cost: onEvent('cost'),
          facts: onEvent('facts'),
          activity: onEvent('activity'),
          // The session named its model: the model check re-reads.
          model: () => void loadJob(),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          delta: (d: any) => setDelta((prev) => (prev + (d?.text ?? '')).slice(-2000)),
          verdicts: () => loadSide(),
        },
        { lastEventId: lastId.current, onOpen: () => void loadJob() },
      );
    };
    void api
      .get<{ events: LogEvent[]; total: number; earlier: number }>(`/api/jobs/${id}/events?limit=400`)
      .then((r) => {
        if (cancelled) return;
        setEvents(r.events);
        setEarlier(r.earlier);
        lastId.current = r.events.at(-1)?.id ?? 0;
        loadSide();
      })
      .catch((e: unknown) => {
        if (!cancelled) setLogError(e instanceof Error ? e.message : 'The session log did not load.');
      })
      .finally(openLive);
    // Ages on the facts move by themselves; the facts are re-read every 30 s.
    const poll = setInterval(() => {
      void loadJob();
    }, 30_000);
    const tick = setInterval(() => setCostTick((t) => t), 15_000);
    return () => {
      cancelled = true;
      stream?.close();
      clearInterval(poll);
      clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 's' && !e.metaKey && !e.ctrlKey && job?.buttons.includes('ship')) {
        e.preventDefault();
        navigate(`/review/${job.id}`);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [job?.buttons, job?.id, navigate]);

  if (!job) {
    return (
      <Screen title="Job">
        <EmptyState title="Fetching the job…" />
      </Screen>
    );
  }
  const m = meta(job.phase);
  const lm = job.lifecycle ? lifeMeta(job.lifecycle) : m;
  const f = job.facts;
  const other = accounts.find((a) => a.id !== (f.account ?? job.accountId));

  const act = async (kind: Parameters<typeof runAction>[1], payload: Record<string, unknown> = {}, label?: string) => {
    setBusy(true);
    try {
      const r = await runAction(job.id, kind, payload, label ?? BUTTON_LABEL[kind]?.label ?? kind);
      if (r?.job) setJob(r.job);
      else await loadJob();
      return Boolean(r?.ok);
    } finally {
      setBusy(false);
    }
  };
  const press = (button: string) => {
    if (button === 'open_pr') return void window.open(job.prUrl ?? '#', '_blank');
    if (button === 'ship') return navigate(`/review/${job.id}`);
    if (button === 'wait') return;
    void act(button as Parameters<typeof runAction>[1], {});
  };
  const sendFollowUp = () => {
    const text = followUp.trim();
    if (!text) return;
    void act('message', { text }, 'your message').then((ok) => {
      if (ok) setFollowUp('');
    });
  };
  const commitAnswer = (value: string) => {
    if (!job.question || busy) return;
    void act('answer', { questionId: job.question.id, answer: value }, `the answer "${value}"`).then((ok) => {
      if (ok) setAnswer('');
    });
  };
  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post<{ job: Job }>(`/api/jobs/${job.id}/refresh`);
      setJob(r.job);
      loadSide();
    } catch (e) {
      setSideError('refresh', e);
    } finally {
      setRefreshing(false);
    }
  };
  const copyRestore = () => {
    if (!f.snapshotRestore) return;
    void navigator.clipboard?.writeText(f.snapshotRestore).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  const running = job.state === 'running';
  const chatVisible = job.state === 'running' || job.state === 'ended';
  const pendingActions = actions.filter((a) => a.state !== 'done');
  const chatHint = running
    ? job.question
      ? 'Answer above, or tell the session something.'
      : 'Tell the session something.'
    : 'This continues the session with your message.';

  return (
    <>
      <Screen
        eyebrow={`${job.repo !== 'meridian' ? 'DHS demo · ' : ''}${job.kind === 'workshop' ? 'Workshop' : job.mode === 'ship' ? 'Ship' : 'Hold'} · ${(job.modelLabel ?? job.model).replace(/^Claude /, '')}${job.effort ? ` · ${job.effort}` : ''} · ${f.accountLabel}`}
        title={job.title}
        sub={
          <>
            {/* V8 §11: the JOB's state leads, not the session's. A header
                badge reading "Done" over a red CI is the forbidden thing —
                a session event shown as a completion. The rail and the
                ending below still describe the session, which is where
                describing the session belongs. */}
            <Badge tone={lm.tone} pulse={lm.live} testId="job-phase">
              {lm.label}
            </Badge>{' '}
            {job.startedAt ? ` ${minutes(job.startedAt, job.state === 'running' ? null : job.finishedAt)}` : ''}
            {/* referenced so the tick above re-renders this line */}
            <span hidden>{tick}</span>
            {' · '}
            <CostReading usd={job.costUsd} asOf={job.costAsOf} live={job.state === 'running'} />
          </>
        }
        actions={
          <Btn kind="ghost" disabled={refreshing} onClick={() => void refresh()} data-testid="refresh">
            {refreshing ? <Spinner /> : <RefreshCw size={15} />} Read again
          </Btn>
        }
      >
        <Rail phase={job.phase} kind={job.kind} journey={job.repo !== 'meridian' ? 'pages' : 'staging'} reached={railReached(job)} />

        {/* V8 §3: what the JOB is doing, as against what its session is
            doing. A session ending is a normal event and is never shown
            here as a completion. */}
        <LifecycleBanner job={job} />
        <ModelMismatchBanner job={job} />

        {/* ── The facts ────────────────────────────────────────────────── */}
        <Section title="Facts" aside={f.lastEventAt ? `as of ${clock(f.lastEventAt)}` : undefined}>
          <div className="ops-factlist" data-testid="facts">
            <Fact label="Process" value={f.processAlive ? `alive, attempt ${f.attempt}` : job.state === 'running' ? `not running (attempt ${f.attempt})` : job.state === 'queued' ? 'not started' : `ended, attempt ${f.attempt}`} tone={f.processAlive ? 'ok' : job.state === 'running' ? 'bad' : undefined} testId="fact-process" />
            <ModelFact job={job} />
            <Fact label="Last event" value={f.lastEventAt ? `${ago(f.lastEventAt)} ago · ${clock(f.lastEventAt)}` : 'none yet'} testId="fact-last-event" />
            {f.openTool ? (
              <Fact label="Command open" value={`${f.openTool.detail ?? f.openTool.name} · running ${minutes(f.openTool.since)}`} tone="run" testId="fact-open-tool" mono />
            ) : null}
            <Fact label="Last commit" value={f.lastCommitAt ? `${ago(f.lastCommitAt)} ago${f.lastCommitSha ? ` · ${f.lastCommitSha.slice(0, 8)}` : ''}` : 'none on this branch'} testId="fact-last-commit" />
            <Fact
              label="Branch"
              value={f.branchPushed ? (f.unpushedCommits ? `on origin · ${f.unpushedCommits} commit${f.unpushedCommits === 1 ? '' : 's'} not yet pushed` : `on origin${f.dirty ? ' · uncommitted changes in the tree' : ''}`) : f.unpushedCommits ? `${f.unpushedCommits} commit${f.unpushedCommits === 1 ? '' : 's'}, not on origin` : job.branch ? 'created, nothing pushed' : 'not created'}
              tone={f.unpushedCommits ? 'hold' : undefined}
              testId="fact-branch"
            />
            {f.snapshotError ? (
              <Fact
                label="Snapshot"
                value={`the safety net has stopped catching: ${f.snapshotError.message}`}
                tone="bad"
                testId="fact-snapshot-error"
              />
            ) : null}
            {f.lastSnapshotAt ? (
              <Fact
                label="Snapshot"
                value={
                  <>
                    {ago(f.lastSnapshotAt)} ago ·{' '}
                    <button type="button" className="ops-linklike" onClick={copyRestore} data-testid="snapshot-restore">
                      <Copy size={12} style={{ verticalAlign: '-2px' }} /> {copied ? 'copied' : 'copy the restore command'}
                    </button>
                  </>
                }
                testId="fact-snapshot"
              />
            ) : null}
            <Fact label="PR" value={job.prNumber ? <a className="ops-linklike" href={job.prUrl ?? '#'} target="_blank" rel="noreferrer">#{job.prNumber}</a> : 'not open'} testId="fact-pr" />
            {job.prNumber ? (
              <Fact
                label="CI"
                value={
                  <>
                    {f.ciConclusion ? `${f.ciConclusion === 'success' ? 'green' : f.ciConclusion === 'failure' ? 'red' : 'running'}` : 'not read yet'}
                    {f.ciLane ? ` · ${f.ciLane} lane` : ''}
                    {f.ciRefreshedAt ? ` · read ${ago(f.ciRefreshedAt)} ago` : ''}
                    {f.ciDigestUrl ? (
                      <>
                        {' · '}
                        <a className="ops-linklike" href={f.ciDigestUrl} target="_blank" rel="noreferrer">the red digest</a>
                      </>
                    ) : null}
                  </>
                }
                tone={f.ciConclusion === 'success' ? 'ok' : f.ciConclusion === 'failure' ? 'bad' : f.ciConclusion === 'pending' ? 'run' : undefined}
                testId="fact-ci"
              />
            ) : null}
            {/* ci-fix §8: a push is an event, so the page says what the daemon
                is waiting for rather than leaving an unpushed commit looking
                like a stall. */}
            {f.unpushedCommits > 0 || f.pushPointAt ? (
              <Fact
                label="Push"
                value={
                  f.pushPointAt
                    ? `push point marked${f.pushPointWhy ? `: ${f.pushPointWhy}` : ''}`
                    : `${f.unpushedCommits} commit${f.unpushedCommits === 1 ? '' : 's'} waiting for a push point`
                }
                tone={f.pushPointAt ? 'run' : undefined}
                testId="fact-push"
              />
            ) : null}
            {f.preflight ? (
              <Fact
                label="Preflight"
                value={`${f.preflight.ok ? 'green' : 'RED'} · ${f.preflight.summary}${f.preflight.strikes ? ` · ${f.preflight.strikes} in a row` : ''}`}
                tone={f.preflight.ok ? 'ok' : 'bad'}
                testId="fact-preflight"
              />
            ) : null}
            <Fact label="Merged" value={f.merged ? <>yes · <Sha value={f.mergeSha} /></> : 'no'} tone={f.merged ? 'ok' : undefined} testId="fact-merged" />
            {f.deploy ? (
              <Fact
                label={f.deploy.kind === 'pages' ? 'Deploy' : 'Staging'}
                value={
                  f.deploy.kind === 'pages'
                    ? f.deploy.conclusion === 'success'
                      ? <>live · <a className="ops-linklike" href={f.deploy.liveUrl ?? '#'} target="_blank" rel="noreferrer">{(f.deploy.liveUrl ?? '').replace('https://', '')}</a></>
                      : f.deploy.status
                        ? <>{f.deploy.conclusion ?? f.deploy.status}{f.deploy.url ? <> · <a className="ops-linklike" href={f.deploy.url} target="_blank" rel="noreferrer">the run</a></> : null}</>
                        : 'no run seen yet'
                    : f.deploy.verifiedSha
                      ? <>verified · <Sha value={f.deploy.verifiedSha} /></>
                      : f.deploy.status
                        ? `${f.deploy.conclusion ?? f.deploy.status} on main`
                        : 'no run seen yet'
                }
                tone={f.deploy.conclusion === 'success' || f.deploy.verifiedSha ? 'ok' : f.deploy.conclusion === 'failure' ? 'bad' : undefined}
                testId="fact-deploy"
              />
            ) : null}
            {f.budgetUsd || f.budgetHours ? <Fact label="Budget" value={`${f.budgetUsd ? `$${f.budgetUsd}` : ''}${f.budgetUsd && f.budgetHours ? ' · ' : ''}${f.budgetHours ? `${f.budgetHours} h` : ''} per attempt`} testId="fact-budget" /> : null}
          </div>
        </Section>

        {/* ── The open question or permission prompt ────────────────────── */}
        {job.question ? (
          <Section title={job.question.kind === 'permission' ? 'The session asks permission' : 'The session asks'}>
            <div
              className="ops-card ops-needs-input"
              style={{ borderColor: 'var(--ops-accent)' }}
              data-testid="question-card"
              data-kind={job.question.kind}
              ref={(el) => {
                if (el && job.question && !questionSeen.current.has(job.question.id)) {
                  questionSeen.current.add(job.question.id);
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
            >
              <p style={{ margin: '0 0 10px', fontSize: 14.5, whiteSpace: 'pre-wrap' }} data-testid="question-text">
                {job.question.text}
              </p>
              {job.question.kind === 'permission' ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Btn kind="primary" disabled={busy} data-testid="permission-allow" onClick={() => void act('answer', { questionId: job.question!.id, decision: 'allow' }, 'allow')}>
                    Allow
                  </Btn>
                  <Btn kind="danger" disabled={busy} data-testid="permission-deny" onClick={() => void act('answer', { questionId: job.question!.id, decision: 'deny', message: 'Denied from the phone.' }, 'deny')}>
                    Deny
                  </Btn>
                </div>
              ) : (
                <>
                  {job.question.options.length ? (
                    <div className="ops-chips" style={{ marginBottom: 10 }} data-testid="question-choices">
                      {job.question.options.map((o) => (
                        <button
                          key={o}
                          type="button"
                          className="ops-chip ops-chip--commit"
                          disabled={busy}
                          onClick={() => commitAnswer(o)}
                          data-testid={`question-choice-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="ops-input"
                      placeholder={job.question.options.length ? 'Or type another answer' : 'Your answer'}
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && answer.trim() && !busy) {
                          e.preventDefault();
                          commitAnswer(answer.trim());
                        }
                      }}
                      data-testid="question-freeform"
                    />
                    <Btn kind="primary" disabled={busy || !answer.trim()} onClick={() => commitAnswer(answer.trim())}>
                      <SendHorizontal size={16} />
                    </Btn>
                  </div>
                </>
              )}
            </div>
          </Section>
        ) : null}

        {/* ── How the last attempt ended, with the buttons that apply ───── */}
        {job.state === 'ended' && job.ending ? (
          <Section title={`Attempt ${f.attempt} ended`}>
            <div className="ops-card" style={{ borderColor: job.ending.kind === 'done' ? 'var(--ops-ok)' : 'var(--ops-bad)' }} data-testid="ending-card" data-kind={job.ending.kind}>
              <p style={{ margin: '0 0 6px', fontSize: 14.5 }}>
                <b>{ENDING_TITLE[job.ending.kind] ?? 'It ended'}.</b> {job.ending.detail}
              </p>
              {job.ending.resetAt ? (
                <p style={{ margin: '0 0 6px', fontSize: 13.5 }} data-testid="ending-reset">
                  The limit resets at {clock(job.ending.resetAt)} ({new Date(job.ending.resetAt).toLocaleDateString([], { day: 'numeric', month: 'short' })}).
                </p>
              ) : null}
              {job.ending.status ? <p className="ops-mono-sub">HTTP {job.ending.status}</p> : null}
              {job.ending.exitCode != null || job.ending.signal ? (
                <p className="ops-mono-sub" data-testid="ending-exit">
                  exit {job.ending.exitCode ?? '?'}
                  {job.ending.signal ? ` · ${job.ending.signal}` : ''} · {ago(job.ending.at)} ago
                </p>
              ) : null}
              {job.ending.raw ? (
                <div className="ops-terminal-raw">
                  <button type="button" onClick={() => setRawOpen((v) => !v)} data-testid="ending-raw-toggle">
                    {rawOpen ? 'Hide what the CLI said' : 'What the CLI said'}
                  </button>
                  {rawOpen ? <pre data-testid="ending-raw">{job.ending.raw}</pre> : null}
                </div>
              ) : null}
            </div>
          </Section>
        ) : null}

        {/* ── Requests: pending, done, failed with a retry ──────────────── */}
        {pendingActions.length ? (
          <div className="ops-actionstate" data-testid="action-state">
            {pendingActions.map((a) => (
              <div key={a.id} className={`ops-actionstate__item ops-actionstate__item--${a.state}`} data-state={a.state} data-kind={a.kind}>
                <span>
                  {a.state === 'pending' ? <Spinner /> : null} <b>{a.label}</b>
                  {a.state === 'pending' ? (a.attempts > 1 ? ` · no answer yet, trying again (${a.attempts})` : ' · sending') : a.state === 'failed' ? ` did not go through. ${a.error ?? ''}` : ' done'}
                </span>
                {a.state === 'failed' ? (
                  <>
                    <button type="button" onClick={() => retryAction(a.id)}>
                      Try again
                    </button>
                    <button type="button" className="ops-actionerror__dismiss" onClick={() => dismissAction(a.id)}>
                      Dismiss
                    </button>
                  </>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {Object.keys(sideErrors).length ? (
          <div className="ops-sideerrors" role="status" data-testid="side-errors">
            Some of this page did not load: <b>{Object.keys(sideErrors).join(', ')}</b>. {Object.values(sideErrors)[0]}
            <button
              type="button"
              onClick={() => {
                setSideErrors({});
                void loadJob();
                loadSide();
              }}
            >
              Try again
            </button>
          </div>
        ) : null}

        <Section title="Actions" aside={other && job.buttons.includes('continue_other') ? `other account: ${other.label}` : undefined}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} data-testid="actions">
            {job.buttons
              .filter((b) => b !== 'answer')
              .map((b) => {
                const def = BUTTON_LABEL[b];
                if (!def) return null;
                const label = b === 'continue_other' && other ? `Continue on ${other.label}` : b === 'wait' && job.ending?.resetAt ? `Resets at ${clock(job.ending.resetAt)}` : def.label;
                return (
                  <Btn key={b} kind={def.kind} disabled={busy || b === 'wait'} onClick={() => press(b)} data-testid={`action-${b}`}>
                    {def.icon} {label}
                  </Btn>
                );
              })}
            {job.state !== 'closed' ? (
              <Btn kind="ghost" onClick={() => setRailsOpen(true)} data-testid="guard-rails">
                <Wallet size={15} /> Budget and accounts
              </Btn>
            ) : null}
          </div>
        </Section>

        <FinisherHistory job={job} />
        <Breakers job={job} onChanged={() => void refresh()} />
        <ModelControl job={job} models={models} accounts={accounts} onChanged={() => void refresh()} />
        <LongJobsPanel job={job} />
        <AlertsPanel job={job} />

        <div className="ops-cols">
          <div>
            {events.some((e) => e.kind === 'phase') ? (
              <Section title="Timeline">
                <div className="ops-card">
                  <PhaseTimeline events={events} kind={job.kind} repo={job.repo} currentPhase={job.phase} />
                </div>
              </Section>
            ) : null}

            <Section title="Session log">
              <LogPanel
                events={events}
                delta={delta}
                costUsd={job.costUsd}
                live={running}
                earlier={earlier}
                loadingEarlier={loadingEarlier}
                onLoadEarlier={() => void loadEarlier()}
                error={logError}
                onRetry={() => {
                  setLogError(null);
                  void api
                    .get<{ events: LogEvent[]; earlier: number }>(`/api/jobs/${id}/events?limit=400`)
                    .then((r) => {
                      setEvents(r.events);
                      setEarlier(r.earlier);
                    })
                    .catch((e: unknown) => setLogError(e instanceof Error ? e.message : 'The session log did not load.'));
                }}
              />
            </Section>

            {attachments.length ? (
              <Section title="Attached files" aside={`${attachments.length}`}>
                <div className="ops-attachments">
                  {attachments.map((a) => (
                    <a
                      key={a.id}
                      className={`ops-attachment${a.state === 'failed' ? ' ops-attachment--failed' : ''}`}
                      href={`/api/jobs/${job.id}/attachments/${a.id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`job-attachment-${a.state ?? 'stored'}`}
                    >
                      <Paperclip size={13} />
                      <span className="ops-attachment__name">{a.filename}</span>
                      <span className="ops-attachment__size">{a.state === 'delivered' ? 'delivered' : a.state === 'failed' ? 'failed' : 'verified'}</span>
                    </a>
                  ))}
                </div>
              </Section>
            ) : null}
          </div>

          <div>
            {job.repo !== 'meridian' && job.kind === 'change' ? (
              <Section title="CI">
                <p className="ops-hint" style={{ margin: 0 }} data-testid="no-ci-note">
                  This repository has no CI. The session's own verification and your review are the whole gate.
                </p>
              </Section>
            ) : grid ? (
              <Section title="CI" aside={f.headSha ? <Sha value={f.headSha} /> : null}>
                <ShardGrid jobId={job.id} grid={grid} />
              </Section>
            ) : null}

            {f.deploy ? (
              <Section title={f.deploy.kind === 'pages' ? 'Deployment' : 'Staging'}>
                <DeployPanel deploy={f.deploy} />
              </Section>
            ) : null}

            {artifacts.length ? (
              <Section title={job.kind === 'workshop' ? 'Deliverables' : 'Artifacts'} aside={`${artifacts.length}`}>
                <ArtifactsPanel jobId={job.id} artifacts={artifacts} />
              </Section>
            ) : null}

            {shots.length > 0 ? (
              <Section title="Screenshots" aside={shotDuplicates ? `${shotTotal} distinct · ${shotDuplicates} identical not stored` : `${shotTotal || shots.length}`}>
                <ShotGrid
                  jobId={job.id}
                  repo={job.repo}
                  shots={shots}
                  groups={shotGroups}
                  total={shotTotal || shots.length}
                  loadingMore={shotsLoading}
                  onNeedMore={() => {
                    if (shotsLoading || shots.length >= (shotTotal || 0)) return;
                    setShotsLoading(true);
                    void api
                      .get<{ shots: Shot[]; total: number; groups: ShotGroup[] }>(`/api/jobs/${id}/shots?limit=200&offset=${shots.length}`)
                      .then((r) => {
                        setShots((cur) => [...cur, ...r.shots]);
                        setShotTotal(r.total);
                        setShotGroups(r.groups);
                      })
                      .catch((e: unknown) => setSideError('shots', e))
                      .finally(() => setShotsLoading(false));
                  }}
                />
              </Section>
            ) : null}

            {diff ? (
              <Section title="Changed files" aside={`+${diff.total.additions} −${diff.total.deletions}`}>
                <DiffList jobId={job.id} files={diff.files} />
              </Section>
            ) : null}

            {commits.length > 0 ? (
              <Section title="Commits" aside={`${commits.length}${commits.some((c) => c.localOnly) ? ` · ${commits.filter((c) => c.localOnly).length} only on the box` : ''}`}>
                <CommitList commits={commits} />
              </Section>
            ) : null}

            <Section title="Cost">
              <CostPanel jobId={job.id} refreshKey={costTick} />
            </Section>

            {job.attempts.length > 1 ? (
              <Section title="Attempts" aside={`${job.attempts.length}`}>
                <div className="ops-card" data-testid="attempts">
                  {job.attempts.map((a) => (
                    <div key={a.id} className="ops-ci__row">
                      <span className="ops-sha">{a.n}</span>
                      <span className="ops-ci__name" style={{ fontFamily: 'var(--ops-font)', fontSize: 13 }}>
                        {a.accountLabel}
                        {a.resumed ? ' · resumed' : ''} · {minutes(a.startedAt, a.endedAt)}
                        {a.ending ? ` · ${meta(a.ending.kind).label.toLowerCase()}` : a.alive ? ' · running' : ''}
                      </span>
                      <span>{a.costUsd != null ? `$${a.costUsd.toFixed(2)}` : ''}</span>
                    </div>
                  ))}
                </div>
              </Section>
            ) : null}

            <Section title="The daemon's record">
              <JournalPanel jobId={job.id} />
            </Section>
          </div>
        </div>
        {chatVisible ? <div className="ops-chatbar__clearance" data-testid="chat-clearance" /> : null}
      </Screen>

      {chatVisible ? (
        <div className="ops-chatbar" data-testid="chat-bar" data-phase={job.phase}>
          {/* V8 §11: a message written down and not yet echoed by the CLI is
              WAITING, not lost. The old wording said it had not gone through
              while the daemon was about to deliver it. */}
          {f.messagePending ? (
            <p className="ops-chatbar__pending" data-testid="chat-pending">
              Waiting to reach the session. It is written down — the daemon delivers it as soon as the session takes input.
            </p>
          ) : null}
          <textarea
            className="ops-chatbar__input"
            placeholder={chatHint}
            rows={1}
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                sendFollowUp();
              }
            }}
            data-testid="chat-input"
          />
          <button type="button" className="ops-chatbar__send" aria-label="Send to the session" disabled={busy || !followUp.trim()} onClick={sendFollowUp} data-testid="chat-send">
            <SendHorizontal size={18} />
          </button>
        </div>
      ) : null}

      {railsOpen ? (
        <GuardRailsSheet
          job={job}
          accounts={accounts}
          onClose={() => setRailsOpen(false)}
          onChanged={() => {
            void loadJob();
          }}
        />
      ) : null}
    </>
  );
}

/* The model, as the session itself reported it. The label that was picked
   is a request; this row is the answer, and it turns red when they differ. */
function ModelFact(props: { job: Job }) {
  const c = props.job.modelCheck;
  if (!c) return null;
  const said = c.reported ?? c.answered;
  if (c.state === 'mismatch') {
    return <Fact label="Model" value={`asked for ${c.launched}, the session reported ${c.reported && c.reported !== c.launched ? c.reported : c.answered}`} tone="bad" mono testId="fact-model" />;
  }
  if (c.state === 'confirmed' && said) {
    return <Fact label="Model" value={`${modelName(said)} · ${said} · reported by the session`} tone="ok" testId="fact-model" />;
  }
  if (c.state === 'waiting') {
    return <Fact label="Model" value={`launched with ${c.launched} · not reported yet`} testId="fact-model" />;
  }
  return <Fact label="Model" value={`${c.picked.label} · ${c.picked.cli}`} testId="fact-model" />;
}

function Fact(props: { label: string; value: React.ReactNode; tone?: 'ok' | 'bad' | 'run' | 'hold'; mono?: boolean; testId?: string }) {
  return (
    <div className={`ops-factrow${props.tone ? ` ops-factrow--${props.tone}` : ''}`} data-testid={props.testId}>
      <span className="ops-factrow__label">{props.label}</span>
      <span className={`ops-factrow__value${props.mono ? ' ops-factrow__value--mono' : ''}`}>{props.value}</span>
    </div>
  );
}

/* The optional per-job budget (hours and dollars), and whether a limit
   continues on the other account by itself (design §2, §3). */
function GuardRailsSheet(props: { job: Job; accounts: Account[]; onClose: () => void; onChanged: () => void }) {
  const [usd, setUsd] = useState(props.job.budgetUsd ? String(props.job.budgetUsd) : '');
  const [hours, setHours] = useState(props.job.budgetHours ? String(props.job.budgetHours) : '');
  const [auto, setAuto] = useState(props.job.autoContinueOtherAccount);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Sheet title="Budget and accounts" onClose={props.onClose}>
      <p className="ops-hint" style={{ marginTop: 0 }}>
        A budget ends an attempt cleanly when reached; Continue starts a fresh one. Both are off unless you set them.
      </p>
      <Field label="Dollars per attempt">
        <input className="ops-input" inputMode="decimal" value={usd} placeholder="none" onChange={(e) => setUsd(e.target.value)} data-testid="budget-usd" />
      </Field>
      <Field label="Hours per attempt">
        <input className="ops-input" inputMode="decimal" value={hours} placeholder="none" onChange={(e) => setHours(e.target.value)} data-testid="budget-hours" />
      </Field>
      <div className="ops-row" style={{ marginTop: 6 }}>
        <div className="ops-row__main">
          <div className="ops-row__title">Continue on the other account when a limit is hit</div>
          <div className="ops-row__sub">
            {props.accounts.length > 1 ? `${props.accounts.map((a) => a.label).join(' and ')} are configured.` : 'Only one account is configured on the box.'}
          </div>
        </div>
        <button type="button" role="switch" aria-checked={auto} aria-label="continue on the other account" className="ops-toggle" onClick={() => setAuto((v) => !v)} data-testid="auto-continue" />
      </div>
      {error ? <p className="ops-error">{error}</p> : null}
      <div style={{ height: 12 }} />
      <Btn
        kind="primary"
        big
        disabled={busy}
        data-testid="guard-rails-save"
        onClick={async () => {
          setBusy(true);
          setError(null);
          const a = await runAction(props.job.id, 'set_budget', { budgetUsd: usd.trim() ? Number(usd) : null, budgetHours: hours.trim() ? Number(hours) : null }, 'the budget');
          const b = await runAction(props.job.id, 'set_auto_continue', { enabled: auto }, 'the account setting');
          setBusy(false);
          if (a?.ok && b?.ok) {
            props.onChanged();
            props.onClose();
          } else setError(a?.error ?? b?.error ?? 'Not saved.');
        }}
      >
        {busy ? <Spinner /> : 'Save'}
      </Btn>
    </Sheet>
  );
}
